(() => {
  'use strict';

  const root = document.querySelector('[data-crystal-chandelier]');
  if (!root || root.classList.contains('cc-enhanced')) return;
  const q = selector => root.querySelector(selector);
  const all = selector => [...root.querySelectorAll(selector)];
  let config;
  try { config = JSON.parse(q('[data-chandelier-config]').textContent); }
  catch { return; } // Leave the native form usable if enhancement cannot initialize.

  const isId = value => /^[1-9][0-9]*$/.test(String(value));
  const variants = (config.variants || []).filter(item =>
    isId(item.id) && Number.isSafeInteger(item.price) && item.price >= 0,
  ).map(item => ({ ...item, id: String(item.id) }));
  if (!variants.length || !isId(config.productId)) return;

  const byId = id => variants.find(item => item.id === String(id));
  const form = q('#CrystalChandelierForm');
  const variantInput = q('[data-variant]');
  const quantityInput = q('[data-quantity]');
  if (!form || !variantInput || !quantityInput) return;
  const storageKey = 'tl-chandelier-pending:' + config.productId;
  const base = (config.rootUrl || '/').replace(/\/?$/, '/');
  const cartUrl = config.cartUrl || base + 'cart';
  const checkoutUrl = base + 'checkout';
  const selected = () => byId(variantInput.value);
  const quantity = () => Number(quantityInput.value);
  const saleEnd = Date.parse(config.saleEnd || '2026-11-28T08:00:00Z');
  const saleActive = () => Number.isFinite(saleEnd) && Date.now() < saleEnd;
  let saleTimer = null;
  let busy = false;
  let navigating = false;
  let pending = null;
  let storageAvailable = true;
  let currentPhoto = null;
  let photoIndex = -1;

  const capacity = variant => {
    if (!variant?.available) return 0;
    if (!variant.inventoryManagement || variant.inventoryPolicy !== 'deny') return Infinity;
    return Number.isSafeInteger(variant.inventoryQuantity) ? Math.max(0, variant.inventoryQuantity) : 0;
  };
  const money = cents => {
    try { return new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency || 'USD' }).format(cents / 100); }
    catch { return (cents / 100).toFixed(2) + ' ' + (config.currency || 'USD'); }
  };

  function savePending(value) {
    pending = value;
    try {
      if (value) sessionStorage.setItem(storageKey, JSON.stringify(value));
      else sessionStorage.removeItem(storageKey);
      storageAvailable = true;
    } catch { storageAvailable = false; }
  }

  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (saved && saved.version === 1 && /^CC-[a-z0-9-]{8,100}$/i.test(saved.reference)
      && isId(saved.variantId) && Number.isSafeInteger(saved.quantity) && saved.quantity > 0
      && ['sending', 'confirmed'].includes(saved.status)) {
      pending = { ...saved, variantId: String(saved.variantId) };
    }
  } catch { storageAvailable = false; }
  // Confirmed adds are finished intents; only ambiguous requests survive reload/BFCache.
  if (pending?.status === 'confirmed') savePending(null);
  const explicitId = new URL(location.href).searchParams.get('variant');
  variantInput.value = pending?.variantId || (explicitId !== null
    ? byId(explicitId)?.id || ''
    : byId(config.selectedVariantId)?.id || variants.find(item => capacity(item) > 0)?.id || variants[0].id);
  if (pending) quantityInput.value = String(pending.quantity);

  function feedback(message, withCart = false) {
    const status = q('[data-feedback]');
    status.replaceChildren(document.createTextNode(message));
    if (withCart) {
      const link = document.createElement('a');
      link.href = cartUrl;
      link.textContent = ' View or edit your bag.';
      status.append(link);
    }
  }

  function selectionError(cart) {
    const variant = selected();
    if (!variant) return 'Please choose a lampshade finish.';
    if (!capacity(variant)) return 'This finish is currently unavailable. Choose another finish.';
    if (!Number.isSafeInteger(quantity()) || quantity() < 1) return 'Enter a whole quantity of one or more lampshades.';
    const already = (cart?.items || []).filter(line => String(line.variant_id || line.id) === variant.id)
      .reduce((total, line) => total + line.quantity, 0);
    if (quantity() + already > capacity(variant)) {
      return 'Not enough stock for this quantity' + (already ? ' plus the lampshades already in your bag' : '') + '. Reduce the quantity or review your bag.';
    }
    return '';
  }

  function sync() {
    const variant = selected();
    const error = selectionError();
    const locked = busy || Boolean(pending);
    q('[data-price]').textContent = variant ? money(variant.price) : '--';
    const showCompare = Boolean(saleActive() && variant && config.compareLabel && Number.isSafeInteger(variant.compareAt) && variant.compareAt > variant.price);
    q('[data-compare]').hidden = !showCompare;
    q('[data-compare-label]').textContent = config.compareLabel || '';
    q('[data-compare-price]').textContent = showCompare ? money(variant.compareAt) : '';
    q('[data-color-name]').textContent = variant?.title || (pending ? 'Earlier selection' : 'Choose a finish');
    q('[data-stock]').textContent = pending ? 'An earlier selection needs a cart reference check before another lampshade is added.' : error || 'Available. Checkout confirms current stock.';
    q('[data-stock]').dataset.error = String(Boolean(error && !pending));
    all('[data-swatch]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.swatch === variant?.id));
      button.disabled = locked;
    });
    variantInput.disabled = locked;
    quantityInput.readOnly = locked;
    quantityInput.removeAttribute('max');
    if (!pending && variant && Number.isFinite(capacity(variant))) quantityInput.max = String(capacity(variant));
    q('[data-decrease]').disabled = locked || quantity() <= 1;
    q('[data-increase]').disabled = locked || !variant || quantity() >= capacity(variant);
    all('[data-buy], [data-sticky-buy]').forEach(button => {
      button.disabled = busy || (!pending && Boolean(error));
    });
    q('[data-buy-label]').textContent = busy ? 'One moment...' : pending ? 'Check bag & continue' : variant && !capacity(variant) ? 'Unavailable' : 'Buy now';
    q('[data-sticky-label]').textContent = busy ? 'One moment...' : pending ? 'Check bag' : variant && !capacity(variant) ? 'Unavailable' : 'Buy now';
    const total = variant && Number.isSafeInteger(quantity()) && quantity() > 0 ? money(variant.price * quantity()) : '--';
    q('[data-sticky-summary]').textContent = (variant?.title || 'Choose a finish') + ' / ' + total + (quantity() > 1 ? ' total' : '');
    form.setAttribute('aria-busy', String(busy));
  }

  function setBusy(value) { busy = value; sync(); }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        ...options, credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'application/json', ...(options.headers || {}) },
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'Your bag could not be updated. Please review it before trying again.');
        error.definiteRejection = response.status >= 400 && response.status < 500 && ![408, 429].includes(response.status);
        throw error;
      }
      return data;
    } finally { clearTimeout(timer); }
  }

  async function readCart() {
    const cart = await request(base + 'cart.js');
    if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0
      || cart.items.some(line => !isId(line.variant_id || line.id) || !Number.isSafeInteger(line.quantity) || line.quantity < 1)
      || cart.items.reduce((sum, line) => sum + line.quantity, 0) !== cart.item_count) {
      throw new Error('Your bag could not be confirmed. Open the native bag to review it; no extra lampshades were added.');
    }
    q('[data-bag-count]').textContent = String(cart.item_count);
    return cart;
  }

  function recovery(cart) {
    const lines = (cart.items || []).filter(line => line.properties?.['_TENLOVE reference'] === pending.reference);
    if (!lines.length) return 'none';
    return lines.every(line => String(line.variant_id || line.id) === pending.variantId)
      && lines.reduce((sum, line) => sum + line.quantity, 0) === pending.quantity ? 'complete' : 'partial';
  }

  function navigateToCheckout() {
    const link = q('[data-checkout-link]');
    link.hidden = false;
    link.href = checkoutUrl;
    feedback('Your selection is saved. Opening secure checkout. No payment has been taken.', true);
    navigating = true;
    location.assign(checkoutUrl);
  }

  async function add(checkout) {
    if (busy || (!pending && selectionError())) return;
    setBusy(true);
    q('[data-checkout-link]').hidden = true;
    feedback(pending ? 'Checking your saved selection without adding it again...' : 'Adding your lampshade to your existing bag...');
    let sending = false;
    try {
      const cart = await readCart();
      const state = pending ? recovery(cart) : 'none';
      if (state === 'partial') throw new Error('The saved selection needs a check. Review your bag; no extra lampshades have been added.');
      if (state === 'none') {
        // Absence on a read cannot prove an earlier timed-out POST will not commit later.
        if (pending) throw new Error('The earlier request has not been confirmed yet. Review your bag; we will not resend it and risk adding the lampshade twice.');
        const error = selectionError(cart);
        if (error) throw new Error(error);
        const reference = 'CC-' + (globalThis.crypto?.randomUUID?.() || Date.now().toString(36) + '-' + Math.random().toString(36).slice(2));
        const intent = { version: 1, reference, variantId: selected().id, quantity: quantity(), status: 'sending' };
        savePending(intent);
        if (!storageAvailable) {
          savePending(null);
          throw new Error('Browser storage is unavailable, so a safe retry cannot be saved. Please use the native bag or enable session storage before ordering here.');
        }
        sync();
        const body = new FormData(form);
        // Locked native controls are omitted from FormData; use the frozen intent explicitly.
        body.set('id', intent.variantId);
        body.set('quantity', String(intent.quantity));
        body.set('properties[_TENLOVE reference]', intent.reference);
        body.delete('return_to');
        sending = true;
        const result = await request(base + 'cart/add.js', { method: 'POST', body });
        sending = false;
        if (recovery({ items: Array.isArray(result.items) ? result.items : [result] }) !== 'complete') {
          throw new Error('The cart response needs a reference check. Review your bag before continuing; this selection will not be resent.');
        }
        q('[data-bag-count]').textContent = String(cart.item_count + intent.quantity);
      }
      savePending({ ...pending, status: 'confirmed' });
      if (checkout) {
        // No discount acknowledgement or post-save GET may hold a confirmed add hostage.
        navigateToCheckout();
      } else {
        const count = pending.quantity;
        savePending(null);
        feedback('Added ' + count + (count === 1 ? ' lampshade' : ' lampshades') + ' to your bag.', true);
      }
    } catch (error) {
      navigating = false;
      if (sending && error.definiteRejection && pending) {
        try { if (recovery(await readCart()) === 'none') savePending(null); }
        catch { /* A failed reconciliation retains the intent and prevents a duplicate POST. */ }
      }
      feedback(error.name === 'AbortError'
        ? 'That request took longer than expected. Check your saved selection or review your bag; we will not add it twice.'
        : error.message || 'Please review your bag before trying again.', true);
    } finally { if (!navigating) setBusy(false); }
  }

  form.addEventListener('submit', event => {
    if (event.submitter && !event.submitter.matches('[data-buy], [data-sticky-buy]')) return;
    event.preventDefault();
    add(true);
  });
  quantityInput.addEventListener('input', sync);
  quantityInput.addEventListener('change', sync);
  q('[data-decrease]').addEventListener('click', () => {
    if (busy || pending) return;
    quantityInput.value = String(Math.max(1, (Number.isSafeInteger(quantity()) ? quantity() : 1) - 1));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  });
  q('[data-increase]').addEventListener('click', () => {
    if (busy || pending) return;
    quantityInput.value = String((Number.isSafeInteger(quantity()) ? quantity() : 0) + 1);
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  });

  function safePhotoUrl(value) {
    if (!value) return '';
    try { const url = new URL(value, location.href); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; }
    catch { return ''; }
  }

  function setPhoto(photo, thumb = null) {
    const image = q('[data-main-photo]');
    const src = safePhotoUrl(photo?.src);
    currentPhoto = src ? { ...photo, src } : null;
    image.removeAttribute('srcset');
    let placeholder = q('[data-photo-placeholder]');
    if (!placeholder) {
      placeholder = document.createElement('span');
      placeholder.className = 'cc-photo-placeholder';
      placeholder.dataset.photoPlaceholder = '';
      q('[data-enlarge]').append(placeholder);
    }
    image.hidden = !src;
    placeholder.hidden = Boolean(src);
    placeholder.textContent = 'Image unavailable for this finish.';
    if (src) {
      image.src = src;
      if (safePhotoUrl(photo.small)) image.srcset = safePhotoUrl(photo.small) + ' 600w, ' + src + ' 1200w';
      image.alt = photo.alt || selected()?.title || 'Chandelier lampshade image';
      q('[data-enlarge]').href = src;
    } else {
      image.removeAttribute('src');
      image.alt = '';
      q('[data-enlarge]').href = '#CrystalChandelierPurchase';
    }
    all('[data-thumb]').forEach(item => item.setAttribute('aria-current', String(item === thumb)));
    if (q('[data-lightbox]').open) updateLightbox();
  }

  function updateVariantPhoto() {
    photoIndex = -1;
    setPhoto(selected()?.image);
  }

  function updateUrl() {
    if (!selected()) return;
    const url = new URL(location.href);
    url.searchParams.set('variant', selected().id);
    history.replaceState(history.state, '', url);
  }

  variantInput.addEventListener('change', () => {
    if (busy || pending) return;
    updateVariantPhoto();
    updateUrl();
    sync();
    root.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: selected() } }));
  });
  all('[data-swatch]').forEach(button => button.addEventListener('click', () => {
    if (busy || pending) return;
    variantInput.value = button.dataset.swatch;
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }));
  q('[data-swatches]').addEventListener('keydown', event => {
    if (busy || pending || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const buttons = all('[data-swatch]');
    const index = buttons.indexOf(document.activeElement);
    if (index < 0) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
      : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next].focus();
    buttons[next].click();
  });

  const thumbs = all('[data-thumb]');
  function selectGalleryPhoto(index) {
    if (!thumbs.length) return;
    photoIndex = (index + thumbs.length) % thumbs.length;
    const thumb = thumbs[photoIndex];
    setPhoto({ src: thumb.dataset.full, alt: thumb.dataset.alt }, thumb);
  }
  thumbs.forEach((thumb, index) => thumb.addEventListener('click', event => {
    event.preventDefault();
    selectGalleryPhoto(index);
  }));
  q('.cc-thumbs')?.addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    selectGalleryPhoto(event.key === 'Home' ? 0 : event.key === 'End' ? thumbs.length - 1
      : photoIndex + (event.key === 'ArrowRight' ? 1 : -1));
    thumbs[photoIndex].focus();
  });
  const lightbox = q('[data-lightbox]');
  function updateLightbox() {
    const image = lightbox.querySelector('img');
    if (currentPhoto) {
      image.src = currentPhoto.src;
      image.alt = currentPhoto.alt || 'Chandelier lampshade image';
      q('[data-lightbox-caption]').textContent = image.alt;
    }
  }
  q('[data-enlarge]').addEventListener('click', event => {
    if (!currentPhoto || !lightbox.showModal) return;
    event.preventDefault();
    updateLightbox();
    lightbox.showModal();
  });
  q('[data-close-photo]').addEventListener('click', () => lightbox.close());
  lightbox.addEventListener('click', event => { if (event.target === lightbox) lightbox.close(); });
  lightbox.addEventListener('close', () => q('[data-enlarge]').focus({ preventScroll: true }));
  lightbox.addEventListener('keydown', event => {
    if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
      selectGalleryPhoto(photoIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }
  });

  // The external sticky submit button uses this same native form. No second click path.
  window.addEventListener('popstate', () => {
    if (busy || pending) return;
    const id = new URL(location.href).searchParams.get('variant');
    variantInput.value = id === null ? config.selectedVariantId : byId(id)?.id || '';
    updateVariantPhoto();
    sync();
  });
  window.addEventListener('pageshow', () => {
    navigating = false;
    if (pending?.status === 'confirmed') savePending(null);
    q('[data-checkout-link]').hidden = true;
    setBusy(false);
  });

  function tickSale() {
    const active = saleActive();
    const banner = q('[data-chandelier-bf]');
    if (banner) banner.hidden = !active;
    if (!active) {
      if (saleTimer !== null) clearInterval(saleTimer);
      saleTimer = null;
      all('[data-bf-discount], [data-sale-badge]').forEach(node => { node.hidden = true; });
      q('[data-compare]').hidden = true;
      return;
    }
    const secondsLeft = Math.max(0, Math.ceil((saleEnd - Date.now()) / 1000));
    const units = {
      days: Math.floor(secondsLeft / 86400),
      hours: Math.floor(secondsLeft / 3600) % 24,
      minutes: Math.floor(secondsLeft / 60) % 60,
      seconds: secondsLeft % 60,
    };
    for (const [unit, value] of Object.entries(units)) {
      const node = q('[data-bf-' + unit + ']');
      if (node) node.textContent = String(value).padStart(2, '0');
    }
    const clock = q('[data-bf-clock]');
    if (clock) clock.hidden = false;
  }
  function startSaleTimer() {
    if (saleTimer !== null) clearInterval(saleTimer);
    saleTimer = null;
    tickSale();
    if (saleActive() && !document.hidden) saleTimer = setInterval(tickSale, 1000);
  }
  document.addEventListener('visibilitychange', startSaleTimer);
  window.addEventListener('pageshow', startSaleTimer);

  root.classList.add('cc-enhanced');
  variantInput.tabIndex = -1;
  variantInput.setAttribute('aria-hidden', 'true');
  q('[data-swatches]').hidden = false;
  all('[data-decrease], [data-increase]').forEach(button => { button.hidden = false; });
  updateVariantPhoto();
  sync();
  startSaleTimer();
  if (pending) feedback('An earlier selection needs a reference check before another lampshade is added.', true);
})();
